import { Icon } from '@design/components/icon/Icon';
import { getPriceStatus, PriceStatus } from '@helpers';
import clsx from 'clsx';

import { Tooltip } from '../../tooltip/Tooltip';
import { WithNAFallback } from '../table-cell/WithNAFallback';
import styles from './RevampTable.module.scss';
import {
  type RevampTableColumn,
  type RevampTableData,
  statusIconProps,
} from './RevampTable.types';
import { RevampTableLogo } from './RevampTableLogo';
import { getColumnData, isKnownStatusType } from './utils';

interface RevampTableCellContentProps {
  column: RevampTableColumn;
  data: RevampTableData;
}

const hasCellValue = (value: RevampTableData['value']) => value !== '';

export const RevampTableCellContent = ({
  column,
  data,
}: RevampTableCellContentProps) => {
  const columnData = data.value;
  const priceStatus =
    column.type === 'directional'
      ? getPriceStatus(+columnData)
      : PriceStatus.Default;
  const isKnownStatus =
    column.type === 'status' && isKnownStatusType(columnData);
  const hasValue = hasCellValue(columnData);

  if (column.type === 'entity') {
    return (
      <div className={styles.entityCell}>
        <RevampTableLogo
          logo={data.logo}
          fallbackLabel={columnData}
          className={styles.entityLogo}
        />
        <div className={styles.entityText}>
          <WithNAFallback
            value={
              hasValue && (
                <span className={styles.entityName}>{columnData}</span>
              )
            }
          />
          {data.tooltip && (
            <span className={styles.entityTicker}>{data.tooltip}</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <Tooltip disabled={!data.tooltip} inPortal label={data.tooltip}>
      <div className={styles.cellInner}>
        {data.logo && (
          <RevampTableLogo
            logo={data.logo}
            fallbackLabel={columnData}
            className={styles.inlineLogo}
            alt="logo"
          />
        )}
        <WithNAFallback
          value={
            hasValue && (
              <div
                className={clsx(styles.cellWrapper, {
                  [styles.negative]: priceStatus === PriceStatus.Negative,
                  [styles.positive]: priceStatus === PriceStatus.Positive,
                  [styles.successStatus]:
                    isKnownStatus && columnData === 'Success',
                  [styles.failedStatus]:
                    isKnownStatus && columnData === 'Failed',
                })}
              >
                {isKnownStatus && (
                  <Icon
                    iconName={statusIconProps[columnData].iconName}
                    size={9}
                    fillColor={statusIconProps[columnData].fillColor}
                  />
                )}
                {getColumnData({ column, value: columnData })}
              </div>
            )
          }
        />
      </div>
    </Tooltip>
  );
};
